import React, { useEffect, useMemo, useRef, useState } from 'react';
import Layout from '../../../components/Layout';
import Modal from '../../../components/Modal';
import ApiInspector from '../../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import { apiFetch, apiDownload } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { API_DOCS } from './PianoContiCogePage.api';
import './PianoContiCogePage.css';

const MODULO = 'amministrazione.piano_conti_coge';
const MAX_LIVELLO = 4;

type Natura = 'ATTIVITA' | 'PASSIVITA' | 'PATRIMONIO_NETTO' | 'COSTI' | 'RICAVI' | 'ORDINE';
type Sottotipo = 'GENERICO' | 'CLIENTE' | 'FORNITORE' | 'BANCA' | 'CASSA' | 'IVA' | 'ERARIO' | 'PERSONALE' | 'PATRIMONIO';

interface ContoNode {
  id: number;
  societaId: number;
  parentId: number | null;
  livello: number;
  segmento: string;
  codice: string;
  descrizione: string;
  note: string | null;
  movimentabile: boolean;
  natura: Natura | null;
  sottotipo: Sottotipo | null;
  bancaId: number | null;
  mostraInAnagraficaArticoli: boolean;
  attivo: boolean;
  figli: ContoNode[];
}

interface BancaCombo { id: number; nome: string; iban?: string | null; attiva?: boolean }

const NATURA_LABEL: Record<Natura, string> = {
  ATTIVITA: 'Attività',
  PASSIVITA: 'Passività',
  PATRIMONIO_NETTO: 'Patrimonio netto',
  COSTI: 'Costi',
  RICAVI: 'Ricavi',
  ORDINE: 'Conti d\'ordine',
};

const NATURA_BADGE: Record<Natura, string> = {
  ATTIVITA: 'bop-badge--info',
  PASSIVITA: 'bop-badge--warning',
  PATRIMONIO_NETTO: 'bop-badge--neutral',
  COSTI: 'bop-badge--danger',
  RICAVI: 'bop-badge--success',
  ORDINE: 'bop-badge--neutral',
};

const SOTTOTIPO_LABEL: Record<Sottotipo, string> = {
  GENERICO: 'Generico',
  CLIENTE: 'Cliente',
  FORNITORE: 'Fornitore',
  BANCA: 'Banca',
  CASSA: 'Cassa',
  IVA: 'IVA',
  ERARIO: 'Erario',
  PERSONALE: 'Personale',
  PATRIMONIO: 'Patrimonio',
};

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

interface ModalContoState {
  mode: 'create' | 'edit';
  parent: ContoNode | null;
  node: ContoNode | null;
}

interface FormContoState {
  segmento: string;
  descrizione: string;
  note: string;
  movimentabile: boolean;
  natura: Natura;
  sottotipo: Sottotipo;
  bancaId: string;
  mostraInAnagraficaArticoli: boolean;
}

interface PreviewRow {
  codice: string;
  segmento: string;
  descrizione: string;
  livello: number;
  movimentabile: boolean;
  natura: string | null;
  sottotipo: string | null;
  attivo: boolean;
  note: string | null;
  stato: 'nuovo' | 'aggiornamento' | 'invariato' | 'errore';
  errors: string[];
  diff: string | null;
  _line?: number;
}

interface PreviewSummary {
  totale: number;
  nuovi: number;
  aggiornamenti: number;
  invariati: number;
  errori: number;
}

interface ImportState {
  step: 'upload' | 'preview' | 'apply' | 'done';
  file: File | null;
  preview: PreviewRow[] | null;
  summary: PreviewSummary | null;
  conflictPolicy: 'skip' | 'overwrite';
  applying: boolean;
  error: string;
  result: { creati: number; aggiornati: number; saltati: number } | null;
}

export default function PianoContiCogePage() {
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const canDelete = can(MODULO, 'delete');

  const { societaId, setSocietaId, listaSocieta, isUnica } = useFiltroSocieta();

  const [tree, setTree] = useState<ContoNode[]>([]);
  const [banche, setBanche] = useState<BancaCombo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mostraDisattivi, setMostraDisattivi] = useState(false);
  const [filtroNatura, setFiltroNatura] = useState<'' | Natura>('');
  const [filtroSottotipo, setFiltroSottotipo] = useState<'' | Sottotipo>('');
  const [filtroSoloFoglie, setFiltroSoloFoglie] = useState(false);
  const [ricerca, setRicerca] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const [modalConto, setModalConto] = useState<ModalContoState | null>(null);
  const [formConto, setFormConto] = useState<FormContoState>({
    segmento: '', descrizione: '', note: '', movimentabile: false, natura: 'ATTIVITA', sottotipo: 'GENERICO', bancaId: '', mostraInAnagraficaArticoli: false,
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [modalImport, setModalImport] = useState<ImportState | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ── Conservazione posizione di scroll durante il reload in-place ───────────
  // Il reload (load) mostra "Caricamento…" e collassa l'altezza della tabella,
  // azzerando lo scroll. Catturiamo la posizione prima del reload e la
  // ripristiniamo dopo che l'albero è stato ri-renderizzato.
  const scrollRestoreRef = useRef<number | null>(null);
  const getScrollEl = () => document.querySelector('.layout-content') as HTMLElement | null;
  const reloadPreservandoScroll = () => {
    const el = getScrollEl();
    scrollRestoreRef.current = el ? el.scrollTop : null;
    load();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (loading || scrollRestoreRef.current == null) return;
    const target = scrollRestoreRef.current;
    scrollRestoreRef.current = null;
    const el = getScrollEl();
    if (el) requestAnimationFrame(() => { el.scrollTop = target; });
  }, [loading, tree]);

  // ── Carica albero ──────────────────────────────────────────────────────────
  const collectIds = (nodes: ContoNode[], acc: Set<number>) => {
    for (const n of nodes) { if (n.figli.length) { acc.add(n.id); collectIds(n.figli, acc); } }
  };

  const load = async () => {
    if (!societaId) { setTree([]); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      // @api GET /api/coge/piano-conti?societaId= -> { success: true, data: [{ id, societaId, parentId, livello, segmento, codice, descrizione, note, movimentabile, natura, sottotipo, attivo, figli }] }
      const res = await apiFetch<{ success: boolean; data: ContoNode[] }>(
        `/api/coge/piano-conti?societaId=${societaId}`,
      );
      const data = res.data ?? [];
      setTree(data);
      const exp = new Set<number>();
      collectIds(data, exp);
      setExpanded(exp);
      // @api GET /api/banche?societaId= -> [{ id, nome, iban, attiva }]
      const resB = await apiFetch<BancaCombo[]>(`/api/banche?societaId=${societaId}`);
      setBanche(Array.isArray(resB) ? resB : []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [societaId]);

  // ── Filtri ─────────────────────────────────────────────────────────────────
  // Filtra l'albero: un nodo "passa" se matcha; un padre passa se ha figli che passano.
  const filteredTree = useMemo<ContoNode[]>(() => {
    const q = ricerca.trim().toLowerCase();
    const filterRec = (nodes: ContoNode[]): ContoNode[] => {
      const out: ContoNode[] = [];
      for (const n of nodes) {
        if (!mostraDisattivi && !n.attivo) continue;
        const figliFiltrati = filterRec(n.figli);
        let match = true;
        if (q) {
          match = n.codice.toLowerCase().includes(q) || n.descrizione.toLowerCase().includes(q);
        }
        if (filtroNatura && (n.natura ?? rootNaturaOf(tree, n) ?? '') !== filtroNatura) match = false;
        if (filtroSottotipo && n.sottotipo !== filtroSottotipo) match = false;
        if (filtroSoloFoglie && !n.movimentabile) match = false;

        if (match || figliFiltrati.length > 0) {
          out.push({ ...n, figli: figliFiltrati });
        }
      }
      return out;
    };
    return filterRec(tree);
  }, [tree, ricerca, mostraDisattivi, filtroNatura, filtroSottotipo, filtroSoloFoglie]);

  // ── Toggle espansione ──────────────────────────────────────────────────────
  const toggleNode = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const espandiTutto = () => { const s = new Set<number>(); collectIds(tree, s); setExpanded(s); };
  const comprimiTutto = () => setExpanded(new Set());

  // ── Modale Conto ───────────────────────────────────────────────────────────
  const openCreateClasse = () => {
    setModalConto({ mode: 'create', parent: null, node: null });
    setFormConto({ segmento: prossimoSegmento(tree), descrizione: '', note: '', movimentabile: false, natura: 'ATTIVITA', sottotipo: 'GENERICO', bancaId: '', mostraInAnagraficaArticoli: false });
    setFormError('');
  };

  const openCreateFiglio = (parent: ContoNode) => {
    setModalConto({ mode: 'create', parent, node: null });
    setFormConto({
      segmento: prossimoSegmento(parent.figli),
      descrizione: '', note: '',
      movimentabile: false,
      natura: (parent.natura ?? rootNaturaOf(tree, parent) ?? 'ATTIVITA') as Natura,
      sottotipo: 'GENERICO',
      bancaId: '',
      mostraInAnagraficaArticoli: false,
    });
    setFormError('');
  };

  const openEdit = (node: ContoNode) => {
    setModalConto({ mode: 'edit', parent: null, node });
    setFormConto({
      segmento: node.segmento,
      descrizione: node.descrizione,
      note: node.note || '',
      movimentabile: node.movimentabile,
      natura: (node.natura ?? rootNaturaOf(tree, node) ?? 'ATTIVITA') as Natura,
      sottotipo: (node.sottotipo ?? 'GENERICO') as Sottotipo,
      bancaId: node.bancaId ? String(node.bancaId) : '',
      mostraInAnagraficaArticoli: node.mostraInAnagraficaArticoli ?? false,
    });
    setFormError('');
  };

  const closeModalConto = () => { setModalConto(null); setFormError(''); };

  const salvaConto = async () => {
    if (!modalConto) return;
    const descrizione = formConto.descrizione.trim();
    if (!descrizione) { setFormError('Descrizione obbligatoria'); return; }

    setSaving(true);
    setFormError('');
    try {
      if (modalConto.mode === 'create') {
        const segmento = formConto.segmento.trim();
        if (!segmento) { setFormError('Segmento codice obbligatorio'); setSaving(false); return; }
        if (!/^[A-Za-z0-9]+$/.test(segmento)) {
          setFormError('Il segmento può contenere solo lettere e numeri'); setSaving(false); return;
        }
        if (!modalConto.parent) {
          // Nuova Classe
          // @api POST /api/coge/piano-conti { societaId, parentId, segmento, descrizione, note, movimentabile, natura, sottotipo } -> { success: true, data: { id } }
          await apiFetch('/api/coge/piano-conti', {
            method: 'POST',
            body: JSON.stringify({
              societaId: Number(societaId),
              parentId: null,
              segmento,
              descrizione,
              note: formConto.note.trim() || null,
              natura: formConto.natura,
            }),
          });
        } else {
          await apiFetch('/api/coge/piano-conti', {
            method: 'POST',
            body: JSON.stringify({
              societaId: modalConto.parent.societaId,
              parentId: modalConto.parent.id,
              segmento,
              descrizione,
              note: formConto.note.trim() || null,
              movimentabile: formConto.movimentabile,
              sottotipo: formConto.movimentabile ? formConto.sottotipo : null,
              bancaId: formConto.movimentabile && formConto.sottotipo === 'BANCA' && formConto.bancaId ? Number(formConto.bancaId) : null,
              mostraInAnagraficaArticoli: formConto.movimentabile ? formConto.mostraInAnagraficaArticoli : false,
            }),
          });
        }
      } else if (modalConto.node) {
        const payload: Record<string, unknown> = {
          descrizione,
          note: formConto.note.trim() || null,
        };
        if (modalConto.node.movimentabile) {
          payload.sottotipo = formConto.sottotipo;
          payload.bancaId = formConto.sottotipo === 'BANCA' && formConto.bancaId ? Number(formConto.bancaId) : null;
          payload.mostraInAnagraficaArticoli = formConto.mostraInAnagraficaArticoli;
        }
        // @api PUT /api/coge/piano-conti/:id { descrizione, note, sottotipo, bancaId, mostraInAnagraficaArticoli } -> { success: true, data: { id } }
        await apiFetch(`/api/coge/piano-conti/${modalConto.node.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      }
      closeModalConto();
      reloadPreservandoScroll();
    } catch (e: any) {
      setFormError(e.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const toggleAttivo = async (node: ContoNode) => {
    try {
      // @api PATCH /api/coge/piano-conti/:id/stato { attivo } -> { success: true, data: { id } }
      await apiFetch(`/api/coge/piano-conti/${node.id}/stato`, {
        method: 'PATCH',
        body: JSON.stringify({ attivo: !node.attivo }),
      });
      reloadPreservandoScroll();
    } catch (e: any) {
      setError(e.message || 'Errore nel cambio stato');
    }
  };

  const eliminaConto = async (node: ContoNode) => {
    if (!window.confirm(`Eliminare ${node.codice} — ${node.descrizione}?`)) return;
    try {
      // @api DELETE /api/coge/piano-conti/:id -> { success: true }
      await apiFetch(`/api/coge/piano-conti/${node.id}`, { method: 'DELETE' });
      reloadPreservandoScroll();
    } catch (e: any) {
      window.alert(e.message || 'Errore nell\'eliminazione');
    }
  };

  // ── Export CSV ─────────────────────────────────────────────────────────────
  const esportaCsv = async () => {
    if (!societaId) return;
    try {
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      // @api GET /api/coge/piano-conti/export?societaId= -> CSV (Content-Disposition: attachment)
      await apiDownload(`/api/coge/piano-conti/export?societaId=${societaId}`, `piano_conti_coge_${today}.csv`);
    } catch (e: any) {
      setError(e.message || 'Errore nell\'export');
    }
  };

  // ── Import CSV ─────────────────────────────────────────────────────────────
  const apriImport = () => {
    setModalImport({ step: 'upload', file: null, preview: null, summary: null, conflictPolicy: 'skip', applying: false, error: '', result: null });
  };

  const chiudiImport = () => setModalImport(null);

  const onFileSelected = (file: File | null) => {
    if (!modalImport) return;
    setModalImport({ ...modalImport, file, error: '' });
  };

  const eseguiPreview = async () => {
    if (!modalImport || !modalImport.file || !societaId) return;
    setModalImport({ ...modalImport, error: '', applying: true });
    try {
      const fd = new FormData();
      fd.append('file', modalImport.file);
      fd.append('societaId', String(societaId));
      // @api POST /api/coge/piano-conti/import/preview FormData{ file, societaId } -> { success: true, data: { righe: [...], summary: {...} } }
      const res = await apiFetch<{ success: boolean; data: { righe: PreviewRow[]; summary: PreviewSummary } }>(
        '/api/coge/piano-conti/import/preview',
        { method: 'POST', body: fd as any },
      );
      setModalImport({
        ...modalImport,
        step: 'preview',
        preview: res.data.righe,
        summary: res.data.summary,
        applying: false,
      });
    } catch (e: any) {
      setModalImport({ ...modalImport, error: e.message || 'Errore nel parsing', applying: false });
    }
  };

  const applicaImport = async () => {
    if (!modalImport || !modalImport.preview || !societaId) return;
    const righeApplicabili = modalImport.preview.filter(r => r.stato !== 'errore' && r.stato !== 'invariato');
    if (righeApplicabili.length === 0) {
      setModalImport({ ...modalImport, error: 'Nessuna riga da applicare' });
      return;
    }
    setModalImport({ ...modalImport, applying: true, error: '' });
    try {
      // @api POST /api/coge/piano-conti/import/apply { societaId, conflictPolicy, righe } -> { success: true, data: { creati, aggiornati, saltati } }
      const res = await apiFetch<{ success: boolean; data: { creati: number; aggiornati: number; saltati: number } }>(
        '/api/coge/piano-conti/import/apply',
        {
          method: 'POST',
          body: JSON.stringify({
            societaId: Number(societaId),
            conflictPolicy: modalImport.conflictPolicy,
            righe: righeApplicabili,
          }),
        },
      );
      setModalImport({ ...modalImport, step: 'done', applying: false, result: res.data });
      load();
    } catch (e: any) {
      setModalImport({ ...modalImport, error: e.message || 'Errore nell\'import', applying: false });
    }
  };

  // ── Render righe albero ────────────────────────────────────────────────────
  const renderRows = (nodes: ContoNode[]): React.ReactNode[] => {
    const out: React.ReactNode[] = [];
    for (const n of nodes) {
      const hasFigli = n.figli.length > 0;
      const isOpen = expanded.has(n.id);
      const naturaEff = (n.natura ?? rootNaturaOf(tree, n)) as Natura | null;
      out.push(
        <tr key={`c-${n.id}`} className={n.attivo ? '' : 'pcc-row--inactive'}>
          <td>
            <div className={`pcc-codice-cell pcc-codice-cell--liv${n.livello}`}>
              {hasFigli ? (
                <button type="button" className={`pcc-toggle ${isOpen ? 'pcc-toggle--open' : ''}`} onClick={() => toggleNode(n.id)} aria-label={isOpen ? 'Comprimi' : 'Espandi'}>
                  <RowIcon.Chevron />
                </button>
              ) : (
                <span className="pcc-toggle-spacer" />
              )}
              <span className="pcc-codice">{n.codice}</span>
            </div>
          </td>
          <td>
            <div className="pcc-descrizione-cell">
              <span>{n.descrizione}</span>
              {n.note && <span className="pcc-note">{n.note}</span>}
            </div>
          </td>
          <td>
            <span className={`pcc-tipo-tag pcc-tipo-tag--liv${n.livello} ${n.movimentabile ? 'pcc-tipo-tag--leaf' : ''}`}>
              {n.livello === 1 ? 'Classe' : n.movimentabile ? 'Sottoconto' : `Gruppo · liv. ${n.livello}`}
            </span>
          </td>
          <td>
            {naturaEff ? (
              <span className={`bop-badge ${NATURA_BADGE[naturaEff]}`}>{NATURA_LABEL[naturaEff]}</span>
            ) : '—'}
            {n.sottotipo && n.sottotipo !== 'GENERICO' && (
              <span className="pcc-sottotipo">{SOTTOTIPO_LABEL[n.sottotipo]}</span>
            )}
          </td>
          <td>
            <span className={`bop-badge ${n.attivo ? 'bop-badge--success' : 'bop-badge--neutral'}`}>
              {n.attivo ? 'Attivo' : 'Disattivo'}
            </span>
          </td>
          <td className="bop-text-right">
            <BopRowActions actions={[
              {
                icon: <PlusIcon />, label: 'Aggiungi figlio',
                onClick: () => openCreateFiglio(n),
                variant: 'primary',
                hidden: !canEdit || n.movimentabile || n.livello >= MAX_LIVELLO || !n.attivo,
              },
              { icon: <RowIcon.Edit />, label: 'Modifica', onClick: () => openEdit(n), hidden: !canEdit },
              { icon: <RowIcon.Disable />, label: n.attivo ? 'Disattiva' : 'Riattiva', onClick: () => toggleAttivo(n), hidden: !canEdit },
              { icon: <RowIcon.Trash />, label: 'Elimina', onClick: () => eliminaConto(n), variant: 'danger', hidden: !canDelete || n.figli.length > 0 },
            ]} />
          </td>
        </tr>,
      );
      if (hasFigli && isOpen) out.push(...renderRows(n.figli));
    }
    return out;
  };

  const noSocieta = !societaId;
  const rows = renderRows(filteredTree);

  return (
    <Layout>
      <div className="piano-conti-coge">
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2>Piano dei conti CO.GE</h2>
            <p>Contabilità generale — albero fino a 4 livelli (Classe → gruppi → sottoconto movimentabile)</p>
          </div>
          {canEdit && (
            <div className="bop-flex">
              <button className="bop-btn bop-btn-secondary" onClick={esportaCsv} disabled={noSocieta || tree.length === 0}>
                Esporta CSV
              </button>
              <button className="bop-btn bop-btn-secondary" onClick={apriImport} disabled={noSocieta}>
                Importa CSV
              </button>
              <button className="bop-btn bop-btn-primary" onClick={openCreateClasse} disabled={noSocieta}>
                + Nuova Classe
              </button>
            </div>
          )}
        </div>

        <div className="bop-filter-bar">
          {!isUnica && (
            <select className="bop-select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
              <option value="">Seleziona società…</option>
              {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          )}
          <input
            className="bop-input pcc-search"
            placeholder="Cerca per codice o descrizione…"
            value={ricerca}
            onChange={e => setRicerca(e.target.value)}
          />
          <select className="bop-select" value={filtroNatura} onChange={e => setFiltroNatura(e.target.value as any)}>
            <option value="">Tutte le nature</option>
            {(Object.keys(NATURA_LABEL) as Natura[]).map(n => <option key={n} value={n}>{NATURA_LABEL[n]}</option>)}
          </select>
          <select className="bop-select" value={filtroSottotipo} onChange={e => setFiltroSottotipo(e.target.value as any)}>
            <option value="">Tutti i sottotipi</option>
            {(Object.keys(SOTTOTIPO_LABEL) as Sottotipo[]).map(s => <option key={s} value={s}>{SOTTOTIPO_LABEL[s]}</option>)}
          </select>
          <label className="bop-flex pcc-checkbox-label">
            <input type="checkbox" checked={filtroSoloFoglie} onChange={e => setFiltroSoloFoglie(e.target.checked)} />
            <span>Solo sottoconti</span>
          </label>
          <label className="bop-flex pcc-checkbox-label">
            <input type="checkbox" checked={mostraDisattivi} onChange={e => setMostraDisattivi(e.target.checked)} />
            <span>Mostra anche disattivi</span>
          </label>
          <div className="pcc-spacer">
            <button className="bop-btn bop-btn-secondary" onClick={espandiTutto}>Espandi tutto</button>
            <button className="bop-btn bop-btn-secondary" onClick={comprimiTutto}>Comprimi tutto</button>
          </div>
        </div>

        {error && <div className="bop-inline-error">{error}</div>}

        <div className="bop-card">
          <div className="bop-table-wrapper">
            {noSocieta ? (
              <div className="bop-empty-state">Seleziona una società per gestire il suo piano dei conti CO.GE.</div>
            ) : loading ? (
              <div className="bop-empty-state">Caricamento…</div>
            ) : tree.length === 0 ? (
              <div className="bop-empty-state">
                Nessun conto presente.{canEdit ? ' Inizia creando una Classe (Attivo, Passivo, ecc.) o importa un CSV.' : ''}
              </div>
            ) : rows.length === 0 ? (
              <div className="bop-empty-state">Nessun risultato per i filtri impostati.</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Codice</th>
                    <th>Descrizione</th>
                    <th>Tipo</th>
                    <th>Natura / Sottotipo</th>
                    <th>Stato</th>
                    <th className="bop-text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>{rows}</tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Modale Conto */}
      {modalConto && (
        <Modal
          title={
            modalConto.mode === 'edit'
              ? `Modifica ${modalConto.node?.codice ?? ''} — ${modalConto.node?.descrizione ?? ''}`
              : modalConto.parent
                ? `Nuovo nodo sotto ${modalConto.parent.codice} (liv. ${modalConto.parent.livello + 1})`
                : 'Nuova Classe (livello 1)'
          }
          onClose={closeModalConto}
          footer={
            <>
              <button className="bop-btn bop-btn-secondary" onClick={closeModalConto}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={salvaConto} disabled={saving}>
                {saving ? 'Salvo…' : 'Salva'}
              </button>
            </>
          }
        >
          {formError && <div className="bop-inline-error">{formError}</div>}

          {/* In creazione: segmento + anteprima codice composto */}
          {modalConto.mode === 'create' && (
            <div className="bop-form-row">
              <div className="bop-form-group">
                <label className="bop-label">Segmento codice *</label>
                <input
                  className="bop-input"
                  value={formConto.segmento}
                  onChange={e => setFormConto(f => ({ ...f, segmento: e.target.value }))}
                  placeholder={modalConto.parent ? 'es. 05, 001, 0001…' : 'es. 1, 2, 3…'}
                  autoFocus
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Codice completo</label>
                <input
                  className="bop-input"
                  value={modalConto.parent ? `${modalConto.parent.codice}.${formConto.segmento}` : formConto.segmento}
                  readOnly
                />
              </div>
            </div>
          )}

          {modalConto.mode === 'edit' && (
            <div className="bop-form-row">
              <div className="bop-form-group">
                <label className="bop-label">Codice</label>
                <input className="bop-input" value={modalConto.node?.codice ?? ''} readOnly />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Livello</label>
                <input className="bop-input" value={String(modalConto.node?.livello ?? '')} readOnly />
              </div>
            </div>
          )}

          <div className="bop-form-group">
            <label className="bop-label">Descrizione *</label>
            <input
              className="bop-input"
              value={formConto.descrizione}
              onChange={e => setFormConto(f => ({ ...f, descrizione: e.target.value }))}
              placeholder={modalConto.parent || modalConto.mode === 'edit' ? 'es. Crediti v/clienti, Cassa contanti…' : 'es. Attivo, Passivo, Costi, Ricavi…'}
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Note</label>
            <textarea
              className="bop-input"
              rows={2}
              value={formConto.note}
              onChange={e => setFormConto(f => ({ ...f, note: e.target.value }))}
            />
          </div>

          {/* Classe: natura obbligatoria */}
          {modalConto.mode === 'create' && !modalConto.parent && (
            <div className="bop-form-group">
              <label className="bop-label">Natura *</label>
              <select
                className="bop-select"
                value={formConto.natura}
                onChange={e => setFormConto(f => ({ ...f, natura: e.target.value as Natura }))}
              >
                {(Object.keys(NATURA_LABEL) as Natura[]).map(n => (
                  <option key={n} value={n}>{NATURA_LABEL[n]}</option>
                ))}
              </select>
              <p className="pcc-hint">La natura è ereditata implicitamente da tutti i discendenti della Classe.</p>
            </div>
          )}

          {/* Figlio: scelta gruppo vs sottoconto + natura ereditata */}
          {modalConto.mode === 'create' && modalConto.parent && (
            <>
              <div className="bop-form-group">
                <label className="bop-label">Tipo nodo *</label>
                <div className="pcc-radio-row">
                  <label className="bop-flex pcc-radio">
                    <input
                      type="radio"
                      name="tipoNodo"
                      checked={!formConto.movimentabile}
                      onChange={() => setFormConto(f => ({ ...f, movimentabile: false }))}
                    />
                    <span>Gruppo (raggruppamento, può avere figli)</span>
                  </label>
                  <label className="bop-flex pcc-radio">
                    <input
                      type="radio"
                      name="tipoNodo"
                      checked={formConto.movimentabile}
                      onChange={() => setFormConto(f => ({ ...f, movimentabile: true }))}
                    />
                    <span>Sottoconto (foglia movimentabile in partita doppia)</span>
                  </label>
                </div>
                <p className="pcc-hint">
                  Una volta scelto, il tipo non è più modificabile. Per un Sottoconto, sceglierai il sottotipo qui sotto.
                </p>
              </div>

              {formConto.movimentabile && (
                <>
                  <div className="bop-form-group">
                    <label className="bop-label">Sottotipo *</label>
                    <select
                      className="bop-select"
                      value={formConto.sottotipo}
                      onChange={e => setFormConto(f => ({ ...f, sottotipo: e.target.value as Sottotipo }))}
                    >
                      {(Object.keys(SOTTOTIPO_LABEL) as Sottotipo[]).map(s => (
                        <option key={s} value={s}>{SOTTOTIPO_LABEL[s]}</option>
                      ))}
                    </select>
                    <p className="pcc-hint">Il sottotipo attiva i collegamenti con anagrafiche (Cliente/Fornitore/Banca…) e le automazioni future dei documenti fiscali.</p>
                  </div>

                  {formConto.sottotipo === 'BANCA' && (
                    <div className="bop-form-group">
                      <label className="bop-label">Banca collegata</label>
                      <select
                        className="bop-select"
                        value={formConto.bancaId}
                        onChange={e => setFormConto(f => ({ ...f, bancaId: e.target.value }))}
                      >
                        <option value="">Nessuna (da collegare in seguito)</option>
                        {banche.filter(b => b.attiva !== false).map(b => (
                          <option key={b.id} value={b.id}>{b.nome}{b.iban ? ` — ${b.iban}` : ''}</option>
                        ))}
                      </select>
                      <p className="pcc-hint">Collega questo sottoconto al conto corrente della Banca aziendale (in <strong>Gestione Amministrativa → Banche aziendali</strong>). Facoltativo.</p>
                    </div>
                  )}

                  <div className="bop-form-group">
                    <label className="bop-flex pcc-radio">
                      <input
                        type="checkbox"
                        checked={formConto.mostraInAnagraficaArticoli}
                        onChange={e => setFormConto(f => ({ ...f, mostraInAnagraficaArticoli: e.target.checked }))}
                      />
                      <span>Mostra in anagrafica articoli</span>
                    </label>
                    <p className="pcc-hint">Se attivo, questo sottoconto è selezionabile come "Sottoconto" nella scheda anagrafica degli articoli.</p>
                  </div>
                </>
              )}

              <div className="bop-form-group">
                <label className="bop-label">Natura</label>
                <p className="pcc-hint">
                  Ereditata dalla Classe:{' '}
                  <span className={`bop-badge ${NATURA_BADGE[formConto.natura]}`}>{NATURA_LABEL[formConto.natura]}</span>
                </p>
              </div>
            </>
          )}

          {/* In modifica: sottotipo + flag anagrafica articoli solo se foglia */}
          {modalConto.mode === 'edit' && modalConto.node?.movimentabile && (
            <>
              <div className="bop-form-group">
                <label className="bop-label">Sottotipo</label>
                <select
                  className="bop-select"
                  value={formConto.sottotipo}
                  onChange={e => setFormConto(f => ({ ...f, sottotipo: e.target.value as Sottotipo }))}
                >
                  {(Object.keys(SOTTOTIPO_LABEL) as Sottotipo[]).map(s => (
                    <option key={s} value={s}>{SOTTOTIPO_LABEL[s]}</option>
                  ))}
                </select>
              </div>

              {formConto.sottotipo === 'BANCA' && (
                <div className="bop-form-group">
                  <label className="bop-label">Banca collegata</label>
                  <select
                    className="bop-select"
                    value={formConto.bancaId}
                    onChange={e => setFormConto(f => ({ ...f, bancaId: e.target.value }))}
                  >
                    <option value="">Nessuna (da collegare in seguito)</option>
                    {banche.filter(b => b.attiva !== false).map(b => (
                      <option key={b.id} value={b.id}>{b.nome}{b.iban ? ` — ${b.iban}` : ''}</option>
                    ))}
                  </select>
                  <p className="pcc-hint">Collega questo sottoconto al conto corrente della Banca aziendale (in <strong>Gestione Amministrativa → Banche aziendali</strong>). Facoltativo.</p>
                </div>
              )}

              <div className="bop-form-group">
                <label className="bop-flex pcc-radio">
                  <input
                    type="checkbox"
                    checked={formConto.mostraInAnagraficaArticoli}
                    onChange={e => setFormConto(f => ({ ...f, mostraInAnagraficaArticoli: e.target.checked }))}
                  />
                  <span>Mostra in anagrafica articoli</span>
                </label>
                <p className="pcc-hint">Se attivo, questo sottoconto è selezionabile come "Sottoconto" nella scheda anagrafica degli articoli.</p>
              </div>
            </>
          )}
        </Modal>
      )}

      {/* Modale Import */}
      {modalImport && (
        <Modal
          title="Importa Piano dei conti CO.GE da CSV"
          onClose={chiudiImport}
          size="lg"
          footer={
            <>
              <button className="bop-btn bop-btn-secondary" onClick={chiudiImport}>
                {modalImport.step === 'done' ? 'Chiudi' : 'Annulla'}
              </button>
              {modalImport.step === 'upload' && (
                <button className="bop-btn bop-btn-primary" onClick={eseguiPreview} disabled={!modalImport.file || modalImport.applying}>
                  {modalImport.applying ? 'Analisi…' : 'Analizza file'}
                </button>
              )}
              {modalImport.step === 'preview' && (
                <button className="bop-btn bop-btn-primary" onClick={applicaImport} disabled={modalImport.applying}>
                  {modalImport.applying ? 'Importo…' : 'Importa'}
                </button>
              )}
            </>
          }
        >
          {modalImport.error && <div className="bop-inline-error">{modalImport.error}</div>}

          {modalImport.step === 'upload' && (
            <div className="pcc-import-step">
              <p className="pcc-hint">
                Formato CSV atteso (con intestazione):<br />
                <code>codice;segmento;descrizione;livello;movimentabile;natura;sottotipo;attivo;note</code>
              </p>
              <p className="pcc-hint">
                Le righe vengono importate per livello crescente (prima le Classi, poi i discendenti).
                Per ogni riga il sistema rileva se è nuova, esistente con modifiche, invariata o in errore.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={e => onFileSelected(e.target.files?.[0] || null)}
                className="pcc-file-input"
              />
              {modalImport.file && (
                <p className="pcc-hint">File selezionato: <strong>{modalImport.file.name}</strong> ({(modalImport.file.size / 1024).toFixed(1)} KB)</p>
              )}
            </div>
          )}

          {modalImport.step === 'preview' && modalImport.preview && modalImport.summary && (
            <div className="pcc-import-step">
              <div className="pcc-import-summary">
                <span className="bop-badge bop-badge--info">Totale: {modalImport.summary.totale}</span>
                <span className="bop-badge bop-badge--success">Nuovi: {modalImport.summary.nuovi}</span>
                <span className="bop-badge bop-badge--warning">Aggiornamenti: {modalImport.summary.aggiornamenti}</span>
                <span className="bop-badge bop-badge--neutral">Invariati: {modalImport.summary.invariati}</span>
                <span className="bop-badge bop-badge--danger">Errori: {modalImport.summary.errori}</span>
              </div>

              {modalImport.summary.aggiornamenti > 0 && (
                <div className="bop-form-group">
                  <label className="bop-label">Politica per i conti esistenti</label>
                  <div className="pcc-radio-row">
                    <label className="bop-flex pcc-radio">
                      <input
                        type="radio"
                        checked={modalImport.conflictPolicy === 'skip'}
                        onChange={() => setModalImport({ ...modalImport, conflictPolicy: 'skip' })}
                      />
                      <span>Salta esistenti (importa solo i nuovi)</span>
                    </label>
                    <label className="bop-flex pcc-radio">
                      <input
                        type="radio"
                        checked={modalImport.conflictPolicy === 'overwrite'}
                        onChange={() => setModalImport({ ...modalImport, conflictPolicy: 'overwrite' })}
                      />
                      <span>Sovrascrivi descrizione/note/sottotipo/attivo dei conti esistenti</span>
                    </label>
                  </div>
                </div>
              )}

              <div className="bop-table-wrapper pcc-import-preview-table">
                <table className="bop-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Codice</th>
                      <th>Descrizione</th>
                      <th>Liv.</th>
                      <th>Stato</th>
                      <th>Dettaglio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalImport.preview.map((r, i) => (
                      <tr key={i} className={`pcc-preview-row pcc-preview-row--${r.stato}`}>
                        <td>{r._line ?? i + 2}</td>
                        <td>{r.codice}</td>
                        <td>{r.descrizione}</td>
                        <td>{r.livello}</td>
                        <td>
                          <span className={`bop-badge ${
                            r.stato === 'nuovo' ? 'bop-badge--success'
                            : r.stato === 'aggiornamento' ? 'bop-badge--warning'
                            : r.stato === 'invariato' ? 'bop-badge--neutral'
                            : 'bop-badge--danger'
                          }`}>
                            {r.stato}
                          </span>
                        </td>
                        <td className="pcc-preview-detail">
                          {r.stato === 'errore' ? r.errors.join('; ')
                            : r.stato === 'aggiornamento' ? r.diff
                            : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {modalImport.step === 'done' && modalImport.result && (
            <div className="pcc-import-step">
              <p>Import completato:</p>
              <ul className="pcc-import-result">
                <li><strong>{modalImport.result.creati}</strong> conti creati</li>
                <li><strong>{modalImport.result.aggiornati}</strong> conti aggiornati</li>
                <li><strong>{modalImport.result.saltati}</strong> conti saltati</li>
              </ul>
            </div>
          )}
        </Modal>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}

// Propone il segmento "last + 1" tra i fratelli, rispettando la convenzione di
// numerazione esistente (larghezza/zeri iniziali). Considera solo i segmenti
// puramente numerici; se nessun fratello ha un segmento numerico ritorna ''
// (l'utente digita manualmente — es. codifiche alfanumeriche).
function prossimoSegmento(fratelli: ContoNode[]): string {
  const numerici = fratelli.filter(f => /^\d+$/.test(f.segmento));
  if (numerici.length === 0) return '';
  let max = 0;
  let width = 0;
  for (const f of numerici) {
    const val = parseInt(f.segmento, 10);
    if (val > max) max = val;
    if (f.segmento.length > width) width = f.segmento.length;
  }
  return String(max + 1).padStart(width, '0');
}

// Risale l'albero per trovare la natura della radice (Classe) a partire da un nodo qualsiasi.
function rootNaturaOf(tree: ContoNode[], node: ContoNode): Natura | null {
  if (node.natura) return node.natura;
  // L'albero è denormalizzato in figli: ricostruisci percorso usando codice
  const root = node.codice.split('.')[0];
  const found = tree.find(t => t.codice === root);
  return (found?.natura ?? null) as Natura | null;
}
